import { X } from "lucide-react";
import { DateTime } from "luxon";
import React, { Dispatch, useMemo } from "react";

import {
  IgButton,
  IgChip,
  IgIconButton,
  IgPaper,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import {
  StripeApiSubscriptionItemPairDto,
  StripeApiSubscriptionSchedulePhaseDto,
} from "@intelligems/ig-types";
import { ensureNumber } from "@intelligems/ig-utils";
import { Grid, useTheme } from "@mui/material";

import {
  CreateOrModifySubscriptionReducerType,
  CreateSubscriptionReducerAction,
} from "../subscriptionReducer";

interface PhaseProps {
  phase: StripeApiSubscriptionSchedulePhaseDto;
  index: number;
  payload: CreateOrModifySubscriptionReducerType;
  dispatchPayload: Dispatch<CreateSubscriptionReducerAction>;
  isPending?: boolean;
}

export const Phase = ({
  phase,
  index,
  payload,
  dispatchPayload,
  isPending = false,
}: PhaseProps) => {
  const now = useMemo(() => DateTime.now().toSeconds(), []);

  const theme = useTheme();
  const els = useMemo(() => {
    const seen = new Map<
      string,
      { item: StripeApiSubscriptionItemPairDto; allDeleted: boolean }
    >();

    for (const i of phase.items) {
      if (!seen.has(i.product.id)) {
        seen.set(i.product.id, { item: i, allDeleted: true });
      }
      if (!i.deleted) {
        seen.get(i.product.id)!.allDeleted = false;
      }
    }

    return Array.from(seen.values()).map(({ item, allDeleted }) => (
      <IgTypography
        key={item.id}
        sx={{
          textDecoration: allDeleted ? "line-through" : undefined,
        }}
      >
        {item.product.name}
      </IgTypography>
    ));
  }, [phase.items]);

  const end = Math.max(
    phase.phaseEnd || 0,
    phase.trialEndTs || payload.phases[index + 1]
      ? phase.trialEndTs || payload.phases[index + 1].phaseStart || 0
      : 0
  );

  return (
    <IgPaper
      sx={{
        boxShadow: "none",
        padding: "10px 20px 10px 20px",
        border: isPending ? "2px dashed" : "1px solid",
        borderColor: isPending
          ? theme.palette.warning.main
          : theme.palette.divider,
        backgroundColor: isPending
          ? theme.palette.warning.light + "08"
          : "transparent",
      }}
      elevation={0}
    >
      <Grid container direction={"column"} rowSpacing={1}>
        <Grid container item justifyContent={"space-between"}>
          <Grid item>
            <Grid container item alignItems={"center"} columnSpacing={1}>
              <Grid item>
                {els.map((el, index) => (
                  <span key={index}>
                    {el}
                    {index !== els.length - 1 && ", "}{" "}
                  </span>
                ))}
              </Grid>
              {isPending && (
                <Grid item>
                  <IgChip
                    label="Unsaved"
                    size="small"
                    color="warning"
                    sx={{
                      fontSize: "0.75rem",
                      height: "20px",
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item>
            {phase.phaseStart
              ? DateTime.fromSeconds(
                  ensureNumber(phase.phaseStart)
                ).toLocaleString(DateTime.DATE_SHORT)
              : ""}{" "}
            -{" "}
            {end
              ? DateTime.fromSeconds(end).toLocaleString(DateTime.DATE_SHORT)
              : "Forever"}
          </Grid>
        </Grid>
        <Grid
          container
          item
          columnSpacing={1}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Grid item>
            {payload.phases.length > 1 &&
            index === payload.phases.length - 1 ? (
              <Grid item>
                <IgTooltip title={"Delete Phase"}>
                  <span>
                    <IgIconButton
                      onClick={() =>
                        dispatchPayload({
                          type: "removePhase",
                          value: index,
                        })
                      }
                    >
                      <X size={16} color={theme.igIconColors.error} />
                    </IgIconButton>
                  </span>
                </IgTooltip>
              </Grid>
            ) : null}
            {phase.coupon || phase.trialEndTs ? (
              <Grid container item columnSpacing={1}>
                {phase.coupon ? (
                  <Grid item>
                    <IgTooltip title={phase.coupon.name}>
                      <IgChip
                        label={"Coupon Applied"}
                        size={"small"}
                        color={"accent"}
                        sx={{
                          minWidth: "70px",
                        }}
                      />
                    </IgTooltip>
                  </Grid>
                ) : null}
                {phase.trialEndTs ? (
                  <Grid item>
                    <IgChip
                      label={`Trialing until ${DateTime.fromSeconds(ensureNumber(phase.trialEndTs)).toLocaleString(DateTime.DATE_SHORT)}`}
                      size={"small"}
                      color={"accent"}
                      sx={{
                        minWidth: "70px",
                      }}
                    />
                  </Grid>
                ) : null}
              </Grid>
            ) : null}
          </Grid>
          <Grid item>
            <Grid container item columnSpacing={1} alignItems={"center"}>
              <Grid item>
                <IgTooltip
                  title={
                    phase.phaseEnd && phase.phaseEnd < now
                      ? "This phase has ended"
                      : ""
                  }
                >
                  <IgButton
                    disabled={Boolean(
                      payload.status === "canceled" ||
                        (phase.phaseEnd && phase.phaseEnd < now)
                    )}
                    variant={"text"}
                    onClick={() =>
                      dispatchPayload({
                        type: "setPhaseIndex",
                        value: index,
                      })
                    }
                  >
                    Modify Phase
                  </IgButton>
                </IgTooltip>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </IgPaper>
  );
};
